import React, { FC, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

import { AppGroup, Application } from 'reduxState/store/application/types';
import { ICampaign } from 'reduxState/store/campaigns/types';
import CampaignAppSelector from '../CampaignForms/CampaignAppsSelector/CampaignAppsSelector';
import { Clear } from '@mui/icons-material';
import CreateCampaignForm from '../CampaignForms/CreateCampaignForm/CreateCampaignForm';
import { clearNewCampaignDraft } from '../CampaignForms/hooks/useCampaignForm';

interface ICreateAppGroupCampaign {
  appGroup: AppGroup;
  isModalOpen: boolean;
  campaignToDuplicate: ICampaign | null;
  setCampaignToDuplicate: (campaign: ICampaign | null) => void;
  handleCloseModal: () => void;
}

const CreateAppGroupCampaign: FC<ICreateAppGroupCampaign> = ({
  appGroup,
  campaignToDuplicate,
  isModalOpen,
  setCampaignToDuplicate,
  handleCloseModal,
}) => {
  const [selectedApps, setSelectedApps] = useState<Application[]>([]);
  const [hasSelectedAppsError, setHasSelectedAppsError] = useState(false);
  const [reset, setReset] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [hasFormChanges, setHasFormChanges] = useState(false);

  const requestClose = () => {
    if (hasFormChanges) setShowCloseConfirm(true);
    else handleCloseModal();
  };
  const confirmClose = () => {
    setShowCloseConfirm(false);
    clearNewCampaignDraft();
    handleCloseModal();
  };
  const cancelClose = () => setShowCloseConfirm(false);

  const resetSelectedApps = () => {
    setSelectedApps([]);
    setHasSelectedAppsError(false);
    setReset(true);
    // Reset the reset flag after a short delay to allow the effect to run
    setTimeout(() => setReset(false), 100);
  };

  return (
    <>
    <Dialog
      open={isModalOpen}
      classes={{ paper: 'p-3 sm:p-4 pb-4 w-[calc(100vw-16px)] max-w-[1400px] min-w-0 m-2 sm:m-4 h-auto max-h-[100dvh] overflow-y-auto overflow-x-hidden' }}
      onClose={() => requestClose()}
      fullWidth
      maxWidth="lg"
    >
      <div
        className="absolute top-2 right-2 sm:top-[15px] sm:right-[15px] cursor-pointer z-10 p-1"
        onClick={requestClose}
        role="button"
        aria-label="Close"
        data-testid="ClearIcon"
      >
        <Clear />
      </div>
      <div className="flex flex-col w-full min-w-0 max-w-full overflow-x-hidden p-4 sm:p-6 md:p-8 pb-6 bg-white min-h-0 box-border">
        <h2 className="m-0 mb-4 text-muted-dark-purple">Create {appGroup.adminName} Generic Conquesting Campaign</h2>
        <div className="mb-4">
          <h3 className="text-muted-dark-purple">Applications</h3>
          <CampaignAppSelector
            appGroup={appGroup}
            setSelectedApps={setSelectedApps}
            hasSelectedAppsError={hasSelectedAppsError}
            setHasSelectedAppsError={setHasSelectedAppsError}
            reset={reset}
            campaign={campaignToDuplicate}
          />
        </div>
        <CreateCampaignForm
          selectedApps={selectedApps}
          setHasSelectedAppsError={setHasSelectedAppsError}
          resetSelectedApps={resetSelectedApps}
          onDraftRestored={apps => {
            const idsInGroup = new Set(appGroup.applications.map(a => a.id));
            setSelectedApps(apps.filter(a => idsInGroup.has(a.id)));
          }}
          isCreateModalOpen={isModalOpen}
          campaignToDuplicate={campaignToDuplicate}
          setCampaignToDuplicate={setCampaignToDuplicate}
          handleCloseModal={handleCloseModal}
          onHasFormChangesChange={setHasFormChanges}
        />
      </div>
    </Dialog>
    <Dialog open={showCloseConfirm} onClose={cancelClose}>
      <DialogTitle>Discard changes?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your progress will be lost. Are you sure you want to close?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelClose}>Cancel</Button>
        <Button onClick={confirmClose} color="error" variant="contained" data-testid="discard-confirm-button">
          Discard
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default CreateAppGroupCampaign;
